import { LogoBackdrop } from "@freightsimple/ui-core/Display/LogoBackdrop";
import { useWidth } from "@/Shared/Hooks/useWidth";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { formatPostalCode } from "@/Helpers/formatPostalCode";
import {
  totalWeightForLineItem,
  totalWeightForLineItems,
} from "@/Helpers/lineItemCalculations";
import ShipmentSmallFromTo from "@/Shared/Shipment/CustomComponents/ShipmentSmallFromTo";
import type { ReactNode } from "react";

function Payload_({ children }: { children: ReactNode }) {
  return (
    <div className="text-[12px] font-normal text-content-muted">{children}</div>
  );
}

interface DescribeHandlingUnitsProps {
  lineItems: Array<LineItem | AddLineItemType>;
}

function DescribeHandlingUnits_(props: DescribeHandlingUnitsProps) {
  function totalLineItemHandlingUnits(
    lineItems: Array<LineItem | AddLineItemType>,
  ) {
    let totalHandlingUnits = 0;
    lineItems.forEach((li) => {
      totalHandlingUnits += li.numberHandlingUnits;
    });
    return totalHandlingUnits;
  }

  function handlingUnitForLineItems(
    lineItems: Array<LineItem | AddLineItemType>,
  ) {
    let handlingUnitType: HandlingUnitType | undefined | "Units" = undefined;
    lineItems.forEach((li) => {
      if (handlingUnitType === undefined) {
        handlingUnitType = li.handlingUnitType;
      }

      if (handlingUnitType !== li.handlingUnitType) {
        handlingUnitType = "Units";
      }
    });

    return handlingUnitType;
  }

  const { lineItems } = props;

  if (lineItems.length === 0) {
    return <></>;
  }

  const firstLineItem = lineItems[0];
  if (lineItems.length === 1 && firstLineItem !== undefined) {
    return (
      <div className="flex flex-col flex-nowrap items-center in-[.dashboard-row-narrow]:flex-row in-[.dashboard-row-narrow]:items-start in-[.dashboard-row-narrow]:gap-1">
        <Payload_>
          {firstLineItem.numberHandlingUnits} x {firstLineItem.handlingUnitType}
        </Payload_>
        <Payload_>{totalWeightForLineItem(firstLineItem)} lb</Payload_>
      </div>
    );
  }
  return (
    <div className="flex flex-col flex-nowrap items-center">
      <Payload_>
        {totalLineItemHandlingUnits(lineItems)} x{" "}
        {handlingUnitForLineItems(lineItems)}
      </Payload_>
      <Payload_>{totalWeightForLineItems(lineItems)} lb</Payload_>
    </div>
  );
}

export interface DashboardRowTemplateBaseProps {
  lineItems: Array<AddLineItemType | LineItem>;
  pickupLocation: AddLocationLocationType | Location;
  deliveryLocation: AddLocationLocationType | Location;
  carrierIdentifier: string | undefined;
  onClick: () => void;
  pickupReference: string;
  deliveryReference: string;
}

interface DashboardRowTemplateProps extends DashboardRowTemplateBaseProps {
  colorBar: string;
  state: ReactNode;
  stateSubtitle?: ReactNode | undefined;
  bookedByName?: string | undefined;
  children?: ReactNode | undefined;
}

function ShipmentRowTemplate(props: DashboardRowTemplateProps) {
  const {
    colorBar,
    state,
    stateSubtitle,
    bookedByName,
    children,
    pickupLocation,
    deliveryLocation,
    carrierIdentifier,
    onClick,
    pickupReference,
    deliveryReference,
    lineItems,
  } = props;

  const pickupCity = [
    pickupLocation.address.city,
    pickupLocation.address.stateOrProvinceCode,
  ]
    .filter((value) => Boolean(value))
    .join(", ");
  const deliveryCity = [
    deliveryLocation.address.city,
    deliveryLocation.address.stateOrProvinceCode,
  ]
    .filter((value) => Boolean(value))
    .join(", ");

  // If we don't have a business name, use the postal code
  const pickupBusinessName =
    pickupLocation.businessName ??
    (pickupLocation.address.postalCode
      ? `Picking up from : ${formatPostalCode(
          pickupLocation.address.postalCode,
        )}`
      : "");

  const deliveryBusinessName =
    deliveryLocation.businessName ??
    (deliveryLocation.address.postalCode
      ? `Delivering to : ${formatPostalCode(
          deliveryLocation.address.postalCode,
        )}`
      : "");

  // The state colour strip on the card's left edge — dynamic per shipment
  // state, so it can't be a static Tailwind class.
  const colorBarStyle = { backgroundColor: colorBar };

  // Published as a class so the blocks below (and ShipmentSmallFromTo) can restyle
  // with `in-[.dashboard-row-narrow]:` variants. Measured with useWidth, NOT
  // a CSS container query — containment would re-base fixed-position
  // popovers rendered inside the row.
  const [rowRef, rowWidth] = useWidth();
  const narrow = rowWidth > 0 && rowWidth < 640;

  return (
    <div
      ref={rowRef}
      className={`relative inline-block min-h-[72px] w-full max-w-[1024px] cursor-pointer rounded-[3px] border border-border-strong p-0 hover:border-border ${
        narrow ? "dashboard-row-narrow" : ""
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={function (e) {
        if (e.key === "Enter" || e.key === " ") {
          // preventDefault stops Space from scrolling the page.
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="absolute top-0 left-0 h-full w-4" style={colorBarStyle} />
      {/* On narrow screens the blocks stack left-aligned instead of sitting
       * in one 72px line. */}
      <div className="flex min-h-[72px] flex-row items-center in-[.dashboard-row-narrow]:flex-col in-[.dashboard-row-narrow]:items-start in-[.dashboard-row-narrow]:gap-2 in-[.dashboard-row-narrow]:py-4 in-[.dashboard-row-narrow]:pl-8">
        <div className="flex w-[150px] flex-col flex-nowrap items-start pl-8 in-[.dashboard-row-narrow]:w-auto in-[.dashboard-row-narrow]:pl-0">
          <div className="relative text-[16px] font-medium text-foreground">
            {state}
          </div>
          {stateSubtitle && (
            <div className="text-[12px] font-semibold text-content-muted">
              {stateSubtitle}
            </div>
          )}
          {bookedByName && (
            <div
              className="max-w-[118px] overflow-hidden text-[11px] font-normal text-ellipsis whitespace-nowrap text-content-muted"
              data-testid="row-booked-by"
            >
              Booked by {bookedByName}
            </div>
          )}
        </div>

        <div className="mr-4 flex w-[70px] flex-col flex-nowrap items-center pl-2 in-[.dashboard-row-narrow]:mr-0 in-[.dashboard-row-narrow]:w-auto in-[.dashboard-row-narrow]:items-start in-[.dashboard-row-narrow]:pl-0">
          <DescribeHandlingUnits_ lineItems={lineItems} />
        </div>

        <ShipmentSmallFromTo
          pickupBusinessName={pickupBusinessName}
          pickupCity={pickupCity}
          pickupReference={pickupReference}
          deliveryBusinessName={deliveryBusinessName}
          deliveryCity={deliveryCity}
          deliveryReference={deliveryReference}
        />

        {/* The margin is the point: in dark mode the mark sits on a pale pad,
         * and a column flush against its neighbours puts that pad right up
         * against the route text. 128px rather than the mark's own 108 because
         * the pad's border and inset add 14. shrink-0 keeps the logo from
         * overflowing its own column when the row is tight. Every logo gets
         * the same box, left-aligned, with the logo centred inside it. */}
        <div className="mx-4 flex w-[128px] shrink-0 flex-row items-center justify-start in-[.dashboard-row-narrow]:mx-0 in-[.dashboard-row-narrow]:w-auto">
          {carrierIdentifier && (
            <LogoBackdrop>
              <span className="flex h-[34px] w-[108px] items-center justify-center">
                <img
                  className="max-h-full max-w-full"
                  alt={displayNameForCarrier(carrierIdentifier)}
                  src={`/carrierLogos/${carrierIdentifier}.png`}
                />
              </span>
            </LogoBackdrop>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}
export default ShipmentRowTemplate;
